import {ReactNode, useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Checkbox, Col, Empty, Flex, Row, Segmented, Select, Skeleton, Table, theme, Typography} from "antd";
import {ColumnsType} from "antd/lib/table";
import {skipToken} from "@reduxjs/toolkit/query";
import {capitalize} from "lodash";
import {useGetAssetClimateHazardsEvolutionQuery, useGetClimateVariablesQuery} from "../../../../../redux/api/asset";
import {ClimateHazardsPeriod} from "../../../../../types/api/assets";
import CustomNumericFormat from "../../../../common/customNumeric/CustomNumericFormat";
import CoordinatesAssetMissing from "../../../../common/CoordinatesAssetMissing";
import HelpTooltip from "../../../../common/HelpTooltip";
import ClimateHazardsRadarChart from "./ClimateHazardsRadarChart";
import ClimateVariableHelp, {useHazardLabel} from "../ClimateVariableHelp";
import {
  CLIMATE_HAZARDS_PERIODS,
  ClimateHazardRow,
  ClimateHazardsScale,
  computeClimateHazardRows,
  getClimateHazardsScaleRange,
  getClimateVariables
} from "./climateHazardsEvolution";

const renderValue = (value: number) => <CustomNumericFormat
  value={value}
  displayType={'text'}
  decimalScale={2}
  fixedDecimalScale
/>

/*
 * mode 'period': period selection, one radar and one table for the selected period
 * mode 'allPeriods': one radar for each period, the table shows all periods
 */
export default function ClimateHazardsRadarContent({assetUuid, mode, missingCoordinates = false, viewSelector}: {
  assetUuid: string,
  mode: 'period' | 'allPeriods',
  missingCoordinates?: boolean,
  // rendered on the same row of the other dropdowns
  viewSelector?: ReactNode,
}) {

  const {t} = useTranslation()
  const {
    data,
    isLoading
  } = useGetAssetClimateHazardsEvolutionQuery(!missingCoordinates ? {assetUuid} : skipToken)
  const {data: climateVariablesInfo} = useGetClimateVariablesQuery()
  const getHazardLabel = useHazardLabel()
  const {token} = theme.useToken()

  const [period, setPeriod] = useState<ClimateHazardsPeriod>('near')
  const [selectedCcv, setSelectedCcv] = useState<string[]>([])
  const [scale, setScale] = useState<ClimateHazardsScale>('adapted')

  const climateVariables = useMemo(() => data ? getClimateVariables(data) : [], [data])
  useEffect(() => {
    setSelectedCcv(climateVariables)
  }, [climateVariables]);

  const rows = useMemo(() => data ? computeClimateHazardRows(data, selectedCcv) : [], [data, selectedCcv])

  // every period chart has its own scale, adapted to its values
  const scaleRanges = useMemo(() => Object.fromEntries(CLIMATE_HAZARDS_PERIODS.map(p =>
      [p, getClimateHazardsScaleRange(rows, [p], scale)])) as Record<ClimateHazardsPeriod, ReturnType<typeof getClimateHazardsScaleRange>>,
    [rows, scale])

  const getClimateVariableLabel = useCallback((ccv: string) => {
    const climateVariable = climateVariablesInfo?.find(climateVariable => climateVariable.name === ccv)
    return climateVariable?.short_label || climateVariable?.label || ccv
  }, [climateVariablesInfo])

  const getPeriodLabel = useCallback((p: ClimateHazardsPeriod) => t(`climateChange.periods.${p}`), [t])

  const columns: ColumnsType<ClimateHazardRow> = useMemo(() => {
    const hazardColumn: ColumnsType<ClimateHazardRow>[number] = {
      key: 'hazard',
      title: capitalize(t('climateChange.hazard')),
      fixed: 'left',
      render: (_, row) => {
        const linkedCcv = climateVariablesInfo?.filter(climateVariable =>
          climateVariable.hazards.some(hazard => hazard.code === row.hazardCode)) || []
        return <>
          {row.impacted ?
            getHazardLabel(row.hazardCode) :
            <Typography.Text type={'secondary'} italic>{getHazardLabel(row.hazardCode)}</Typography.Text>}
          {linkedCcv.length > 0 && <HelpTooltip
            tooltip={{
              title: <Flex gap={'0.5rem'} vertical>
                <Typography.Text strong>{getHazardLabel(row.hazardCode)}</Typography.Text>
                <Flex vertical>
                  <Typography.Text strong>{capitalize(t('climateChange.linkedClimateVariables'))}</Typography.Text>
                  <ul style={{margin: 0, paddingLeft: '1.2rem', color: token.colorText}}>
                    {linkedCcv.map(climateVariable => <li key={climateVariable.name}>
                      <Typography.Text type={selectedCcv.includes(climateVariable.name) ? undefined : 'secondary'}>
                        {climateVariable.short_label || climateVariable.label}
                        {!selectedCcv.includes(climateVariable.name) && ` (${t('climateChange.notSelected')})`}
                      </Typography.Text>
                    </li>)}
                  </ul>
                </Flex>
              </Flex>,
              color: 'white',
            }}
            iconStyle={{fontSize: '', marginLeft: '0.2rem'}}
          />}
        </>
      },
    }
    const currentColumn: ColumnsType<ClimateHazardRow>[number] = {
      key: 't0',
      title: capitalize(t('climateChange.current')),
      align: 'right',
      render: (_, row) => renderValue(row.t0),
    }
    const scenarioColumns = (p: ClimateHazardsPeriod): ColumnsType<ClimateHazardRow> => [
      {key: `${p}_ssp245`, title: 'SSP2 4.5', align: 'right', render: (_, row) => row.impacted ? renderValue(row.periods[p].ssp245.value) : '-'},
      {key: `${p}_ssp370`, title: 'SSP3 7.0', align: 'right', render: (_, row) => row.impacted ? renderValue(row.periods[p].ssp370.value) : '-'},
    ]
    if (mode === 'period') {
      return [hazardColumn, currentColumn, ...scenarioColumns(period)]
    }
    return [hazardColumn, currentColumn, ...CLIMATE_HAZARDS_PERIODS.map(p => ({
      key: p,
      title: getPeriodLabel(p),
      children: scenarioColumns(p),
    }))]
  }, [mode, period, t, getHazardLabel, getPeriodLabel, climateVariablesInfo, selectedCcv, token.colorText])

  const table = <Table
    rowKey={'hazardCode'}
    size={'small'}
    bordered={mode === 'allPeriods'}
    scroll={{x: true}}
    columns={columns}
    dataSource={rows}
    pagination={false}
  />

  if (missingCoordinates || isLoading || !data) {
    return <Flex vertical gap={'middle'}>
      {viewSelector}
      {missingCoordinates ?
        <CoordinatesAssetMissing/> :
        isLoading ? <Skeleton active/> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}/>}
    </Flex>
  }

  return <Flex vertical gap={'middle'}>
    <Flex gap={'middle'} align={'center'} wrap={'wrap'}>
      {viewSelector}
      {mode === 'period' &&
        <Flex gap={'small'} align={'center'}>
          <Typography.Text strong>{capitalize(t('climateChange.period'))}:</Typography.Text>
          <Select
            value={period}
            onChange={setPeriod}
            style={{minWidth: 220}}
            options={CLIMATE_HAZARDS_PERIODS.map(p => ({value: p, label: getPeriodLabel(p)}))}
          />
        </Flex>
      }
      <Flex gap={'small'} align={'center'}>
        <Typography.Text strong>{capitalize(t('climateChange.scale'))}:</Typography.Text>
        <Segmented
          value={scale}
          onChange={value => setScale(value as ClimateHazardsScale)}
          options={[
            {value: 'adapted', label: capitalize(t('climateChange.scales.adapted'))},
            {value: 'fixed', label: '0-10'},
          ]}
        />
      </Flex>
    </Flex>
    <Flex vertical gap={'small'}>
      <Typography.Text strong>{capitalize(t('climateChange.climateVariables'))}:</Typography.Text>
      <Checkbox.Group
        value={selectedCcv}
        onChange={values => setSelectedCcv(values as string[])}
        options={climateVariables.map(ccv => {
          const climateVariable = climateVariablesInfo?.find(climateVariable => climateVariable.name === ccv)
          return {
            value: ccv,
            label: <>
              {getClimateVariableLabel(ccv)}
              {climateVariable && <ClimateVariableHelp climateVariable={climateVariable}/>}
            </>,
          }
        })}
      />
    </Flex>
    {selectedCcv.length === 0 ?
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={capitalize(t('climateChange.selectClimateVariable'))}
      /> :
      <>
        {mode === 'period' ?
          // chart and table side by side when there is enough space
          <Row gutter={[24, 16]} align={'middle'}>
            <Col xs={24} xl={13}>
              <ClimateHazardsRadarChart
                rows={rows}
                period={period}
                getHazardLabel={getHazardLabel}
                getClimateVariableLabel={getClimateVariableLabel}
                scaleRange={scaleRanges[period]}
              />
            </Col>
            <Col xs={24} xl={11}>
              {table}
            </Col>
          </Row> :
          <>
            <Row gutter={[16, 16]}>
              {CLIMATE_HAZARDS_PERIODS.map(p => <Col key={p} xs={24} xl={8}>
                <ClimateHazardsRadarChart
                  rows={rows}
                  period={p}
                  getHazardLabel={getHazardLabel}
                  getClimateVariableLabel={getClimateVariableLabel}
                  scaleRange={scaleRanges[p]}
                  title={getPeriodLabel(p)}
                  height={380}
                />
              </Col>)}
            </Row>
            {table}
          </>
        }
        <Typography.Text type={'secondary'} italic>
          {capitalize(t('climateChange.radarNote'))}
        </Typography.Text>
      </>
    }
  </Flex>
}
